import { useEffect, useState } from "react";
import axios from "axios";
import { BsStarFill } from "react-icons/bs";
import {
  MdCheck, MdClose, MdOutlineFlag, MdOutlineCheckCircle
} from "react-icons/md";
import {
  TbMessageCircle, TbCalendar, TbUser, TbBuilding,
  TbChevronLeft, TbChevronRight, TbAlertTriangle,
  TbCircleCheck, TbCircleX, TbFlag
} from "react-icons/tb";

// ── Étoiles ───────────────────────────────────────────────────────
function Stars({ rating, size = 12 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <BsStarFill
          key={i}
          size={size}
          className={i <= rating ? "text-amber-400" : "text-gray-200"}
        />
      ))}
    </div>
  );
}

// ── Score badge ───────────────────────────────────────────────────
function ScoreBadge({ rating }) {
  const colors = {
    5: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    4: "bg-green-50 text-green-700 ring-green-200",
    3: "bg-amber-50 text-amber-700 ring-amber-200",
    2: "bg-orange-50 text-orange-700 ring-orange-200",
    1: "bg-red-50 text-red-700 ring-red-200",
  };
  return (
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-bold ring-1 ${colors[rating] || colors[3]}`}>
      {rating}
    </span>
  );
}

// ── Avatar initiales ──────────────────────────────────────────────
function Avatar({ name, surname }) {
  const initials = `${name?.[0] || ""}${surname?.[0] || ""}`.toUpperCase() || "?";
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-violet-100 text-violet-700",
    "bg-emerald-100 text-emerald-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-cyan-100 text-cyan-700",
  ];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${colors[idx]}`}>
      {initials}
    </div>
  );
}

// ── Format date ───────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtShort = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—";

// ── Card avis ─────────────────────────────────────────────────────
function ReviewCard({ review, actions, isLoading }) {
  const [expanded, setExpanded] = useState(false);
  const commentLong = review.comment?.length > 180;
  const displayComment = commentLong && !expanded
    ? review.comment.slice(0, 180) + "…"
    : review.comment;

  return (
    <div className={`
      bg-white rounded-2xl border transition-all duration-200
      ${isLoading ? "opacity-50 pointer-events-none" : "hover:shadow-md hover:border-gray-200"}
      ${review.hotelFlag?.flaggedAt && !review.hotelFlag?.reviewed
        ? "border-orange-200 bg-orange-50/30"
        : "border-gray-100"
      }
    `}>
      <div className="p-5">
        <div className="flex gap-4">

          {/* Avatar */}
          <Avatar name={review.userId?.name} surname={review.userId?.surname} />

          {/* Contenu */}
          <div className="flex-1 min-w-0">

            {/* Ligne 1 — auteur + score */}
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {review.userId?.name} {review.userId?.surname}
                </p>
                <p className="text-xs text-gray-400">{review.userId?.email}</p>
              </div>
              <ScoreBadge rating={review.rating} />
            </div>

            {/* Étoiles */}
            <div className="mb-2">
              <Stars rating={review.rating} />
            </div>

            {/* Méta infos */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
              {/* Hôtel */}
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <TbBuilding className="shrink-0" />
                {review.hotelId?.name ?? "—"}
              </span>

              {/* Séjour */}
              {review.reservationId?.checkInDate && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <TbCalendar className="shrink-0" />
                  {fmtShort(review.reservationId.checkInDate)} → {fmtShort(review.reservationId.checkOutDate)}
                  {review.reservationId.totalNights && ` · ${review.reservationId.totalNights}n`}
                </span>
              )}

              {/* Réf */}
              <span className="text-xs font-mono text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded">
                {review.reservationId?.reservationId ?? "—"}
              </span>

              {/* Date avis */}
              <span className="text-xs text-gray-400 ml-auto">
                {fmtDate(review.createdAt)}
              </span>
            </div>

            {/* Badge signalement hôtelier */}
            {review.hotelFlag?.flaggedAt && !review.hotelFlag?.reviewed && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-orange-50 border border-orange-100 rounded-xl">
                <TbAlertTriangle className="text-orange-500 shrink-0" size={14} />
                <p className="text-xs text-orange-700 font-medium">
                  Signalé par l'hôtel · <span className="capitalize">{review.hotelFlag.reason}</span>
                </p>
              </div>
            )}

            {/* Commentaire */}
            {review.comment ? (
              <div>
                <p className="text-sm text-gray-700 leading-relaxed">{displayComment}</p>
                {commentLong && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-[var(--k-green)] hover:underline mt-1"
                  >
                    {expanded ? "Voir moins" : "Lire la suite"}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-300 italic flex items-center gap-1">
                <TbMessageCircle /> Sans commentaire
              </p>
            )}

            {/* Réponse hôtel */}
            {review.hotelReply?.text && (
              <div className="mt-3 pl-3 border-l-2 border-[rgb(var(--k-green-rgb)/0.3)] bg-green-50/40 rounded-r-lg py-2 pr-3">
                <p className="text-xs font-medium text-[var(--k-green)] mb-0.5">Réponse de l'établissement</p>
                <p className="text-sm text-gray-600">{review.hotelReply.text}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-end gap-2">
        {actions}
      </div>
    </div>
  );
}

// ── Bouton action ─────────────────────────────────────────────────
function ActionBtn({ onClick, disabled, icon: Icon, label, variant = "ghost" }) {
  const variants = {
    success: "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 ring-emerald-200",
    danger:  "bg-red-50 text-red-500 hover:bg-red-100 ring-red-200",
    ghost:   "bg-gray-50 text-gray-600 hover:bg-gray-100 ring-gray-200",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold ring-1 transition disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]}`}
    >
      {Icon && <Icon size={14} />} {label}
    </button>
  );
}

// ── Select raison ─────────────────────────────────────────────────
function ReasonSelect({ value, onChange }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-gray-200 rounded-lg px-2.5 py-2 text-gray-600 focus:outline-none focus:border-gray-400 bg-white transition"
    >
      <option value="">Motif de retrait…</option>
      <option value="insultes">Insultes</option>
      <option value="spam">Spam</option>
      <option value="faux">Faux avis</option>
      <option value="inapproprié">Inapproprié</option>
      <option value="autre">Autre</option>
    </select>
  );
}

// ── Tabs config ───────────────────────────────────────────────────
const TABS = [
  { key: "pending",  label: "En attente", icon: TbCircleCheck,  accent: "text-amber-500",   bg: "bg-amber-50"   },
  { key: "approved", label: "Approuvés",  icon: TbCircleCheck,  accent: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "flagged",  label: "Signalés",   icon: TbFlag,         accent: "text-red-500",     bg: "bg-red-50"     },
];

// ── Composant principal ───────────────────────────────────────────
export default function ReviewsModeration() {
  const [tab, setTab]                   = useState("pending");
  const [reviews, setReviews]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [total, setTotal]               = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [flagReason, setFlagReason]     = useState({});

  const fetchReviews = async (t = tab, p = 1) => {
    try {
      setLoading(true);
      let url, params = { page: p };
      if (t === "pending")        url = "https://api.komorotel.com/api/reviews/admin/pending";
      else if (t === "approved")  url = "https://api.komorotel.com/api/reviews/admin/approved";
      else { url = "https://api.komorotel.com/api/reviews/admin/approved"; params.flaggedOnly = true; }
      const res = await axios.get(url, { params, withCredentials: true });
      setReviews(res.data.reviews);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
    } catch (err) { console.log(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); fetchReviews(tab, 1); }, [tab]);
  useEffect(() => { fetchReviews(tab, page); }, [page]);

  // ── Actions ───────────────────────────────────────────────────
  const moderate = async (id, action) => {
    try {
      setActionLoading(id);
      await axios.put(`https://api.komorotel.com/api/reviews/${id}/moderate`, { action }, { withCredentials: true });
      setReviews(prev => prev.filter(r => r._id !== id));
      setTotal(prev => prev - 1);
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
    finally { setActionLoading(null); }
  };

  const flag = async (id) => {
    const reason = flagReason[id];
    if (!reason) return alert("Choisissez une raison");
    try {
      setActionLoading(id);
      await axios.put(`https://api.komorotel.com/api/reviews/${id}/flag`, { reason }, { withCredentials: true });
      setReviews(prev => prev.filter(r => r._id !== id));
      setTotal(prev => prev - 1);
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
    finally { setActionLoading(null); }
  };

  const dismissFlag = async (id) => {
    try {
      setActionLoading(id);
      await axios.put(`https://api.komorotel.com/api/reviews/${id}/dismiss-flag`, {}, { withCredentials: true });
      setReviews(prev => prev.filter(r => r._id !== id));
      setTotal(prev => prev - 1);
    } catch (err) { alert(err.response?.data?.message || "Erreur"); }
    finally { setActionLoading(null); }
  };

  // ── Actions par onglet ────────────────────────────────────────
  const renderActions = (review) => {
    const busy = actionLoading === review._id;

    if (tab === "pending") return (
      <>
        <ActionBtn onClick={() => moderate(review._id, "approve")} disabled={busy} icon={MdCheck}   label="Approuver" variant="success" />
        <ActionBtn onClick={() => moderate(review._id, "reject")}  disabled={busy} icon={MdClose}   label="Rejeter"   variant="danger"  />
      </>
    );

    if (tab === "approved") return (
      <>
        <ReasonSelect
          value={flagReason[review._id]}
          onChange={(v) => setFlagReason(prev => ({ ...prev, [review._id]: v }))}
        />
        <ActionBtn
          onClick={() => flag(review._id)}
          disabled={busy || !flagReason[review._id]}
          icon={MdOutlineFlag} label="Retirer" variant="danger"
        />
      </>
    );

    if (tab === "flagged") return (
      <>
        <ActionBtn onClick={() => dismissFlag(review._id)} disabled={busy} icon={MdOutlineCheckCircle} label="Garder" variant="success" />
        <ReasonSelect
          value={flagReason[review._id]}
          onChange={(v) => setFlagReason(prev => ({ ...prev, [review._id]: v }))}
        />
        <ActionBtn
          onClick={() => flag(review._id)}
          disabled={busy || !flagReason[review._id]}
          icon={MdOutlineFlag} label="Retirer" variant="danger"
        />
      </>
    );
  };

  const currentTab = TABS.find(t => t.key === tab);

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6">

      {/* ── HEADER ──────────────────────────────────────────────── */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Modération des avis</h1>
        <p className="text-sm text-gray-400 mt-1">
          Gérez la qualité des avis publiés sur Komorotel
        </p>
      </div>

      {/* ── TABS ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 shadow-sm p-1 rounded-2xl w-fit mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                active
                  ? "bg-gray-900 text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t.label}
              {active && total > 0 && (
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── LOADING ─────────────────────────────────────────────── */}
      {loading && (
        <div className="flex flex-col gap-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-32 bg-white border border-gray-100 rounded-2xl animate-pulse"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      )}

      {/* ── VIDE ────────────────────────────────────────────────── */}
      {!loading && reviews.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {tab === "pending"  && <TbCircleCheck className="text-emerald-500" size={24} />}
            {tab === "approved" && <TbCircleCheck className="text-gray-400" size={24} />}
            {tab === "flagged"  && <TbFlag className="text-emerald-500" size={24} />}
          </div>
          <p className="text-gray-500 text-sm font-medium">
            {tab === "pending"  && "Aucun avis en attente de modération"}
            {tab === "approved" && "Aucun avis approuvé"}
            {tab === "flagged"  && "Aucun signalement en attente"}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            {tab === "pending" && "Tous les avis ont été traités ✓"}
            {tab === "flagged" && "Tous les signalements ont été traités ✓"}
          </p>
        </div>
      )}

      {/* ── LISTE ───────────────────────────────────────────────── */}
      {!loading && reviews.length > 0 && (
        <>
          {/* Compteur */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400">
              {total} avis · page {page}/{totalPages}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {reviews.map((review) => (
              <ReviewCard
                key={review._id}
                review={review}
                actions={renderActions(review)}
                isLoading={actionLoading === review._id}
              />
            ))}
          </div>
        </>
      )}

      {/* ── PAGINATION ──────────────────────────────────────────── */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 transition"
          >
            <TbChevronLeft size={16} />
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === "..." ? (
                <span key={`dot-${i}`} className="text-gray-300 text-sm px-1">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-semibold transition ${
                    page === p
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              ))}
          </div>

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 transition"
          >
            <TbChevronRight size={16} />
          </button>
        </div>
      )}

    </div>
  );
}